import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import {
  MdOutlineEventAvailable,
  MdOutlinePayments,
  MdOutlineSchedule,
} from "react-icons/md";

import EndUserPublicLayout from "../../atoms/EndUserPublicLayout";
import { EmptyState } from "../../atoms/EmptyState";
import { Spinner } from "../../atoms/Spinner";
import MediaCard from "../../molecules/MediaCard";
import { bookingPath } from "../../atoms/BookingLinkButton";
import { usePublicBookingPage } from "../../../hooks/agenda/publicBooking";
import { BookingTypeLocation } from "../customRegister/components/BookingTypeDetails";
import { durationLabel } from "../dashboard/agenda/agendaDates";
import { descriptionExcerpt } from "../dashboard/agenda/agendaTypes";
import { formatPrice } from "../dashboard/agenda/agendaPayment";
import { useBookingMoment } from "../customRegister/components/BookingSlotPicker";
import {
  captureBookingUtmParams,
  withUtmParams,
} from "../customRegister/utmCapture";

/** Navigation state a card hands to the tunnel, which then offers the way back to this page. */
export const FROM_BOOKING_PAGE = "fromBookingPage";

const BookingPage: React.FC = () => {
  const { entityId = "" } = useParams<{ entityId: string }>();
  const { search } = useLocation();
  const { t } = useTranslation("clientView");
  const { lang } = useBookingMoment();
  const member = new URLSearchParams(search).get("member") || undefined;
  const { data, isLoading, isError } = usePublicBookingPage(entityId, member);
  const [utm] = useState(() => captureBookingUtmParams(entityId, search));

  if (isLoading) {
    return (
      <EndUserPublicLayout>
        <div className="flex justify-center py-8" translate="no">
          <Spinner size="md" />
        </div>
      </EndUserPublicLayout>
    );
  }

  const types = data?.types ?? [];
  if (types.length === 1) {
    return (
      <Navigate to={`${bookingPath(entityId, types[0].id)}${search}`} replace />
    );
  }

  const layoutProps = {
    entityLogo: data?.entity.logo,
    entityName: data?.entity.name,
  };

  if (isError || types.length === 0) {
    return (
      <EndUserPublicLayout {...layoutProps}>
        <EmptyState
          icon={<MdOutlineEventAvailable size={24} />}
          title={t("bookingPage.empty")}
        />
      </EndUserPublicLayout>
    );
  }

  return (
    <EndUserPublicLayout
      {...layoutProps}
      title={
        (data?.member?.name
          ? t("bookingPage.titleWith", { name: data.member.name })
          : t("bookingPage.title")) as string
      }
      subtitle={t("bookingPage.subtitle") as string}
    >
      <ul className="flex flex-col gap-4">
        {types.map((type) => {
          const excerpt = descriptionExcerpt(type.description);
          return (
            <li key={type.id} className="relative">
              <MediaCard imageUrl={type.image_url} imageAlt="">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-gray-900">
                    <Link
                      to={withUtmParams(
                        bookingPath(entityId, type.id, member),
                        utm
                      )}
                      state={{ [FROM_BOOKING_PAGE]: true }}
                      className="break-words after:absolute after:inset-0 after:rounded-lg hover:text-blue-700 focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-blue-500"
                    >
                      {type.title}
                    </Link>
                  </h2>
                  {excerpt && (
                    <p className="mt-1 line-clamp-2 text-sm text-gray-600">
                      {excerpt}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="flex items-center gap-2 text-sm text-gray-700">
                    <MdOutlineSchedule
                      size={18}
                      aria-hidden="true"
                      className="shrink-0 text-gray-500"
                    />
                    {durationLabel(
                      t,
                      "bookingTunnel.duration",
                      type.duration_min
                    )}
                  </p>
                  {type.price && (
                    <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <MdOutlinePayments
                        size={18}
                        aria-hidden="true"
                        className="shrink-0 text-gray-500"
                      />
                      {formatPrice(
                        type.price.amount,
                        type.price.currency,
                        lang
                      )}
                    </p>
                  )}
                  <BookingTypeLocation location={type.location} />
                </div>
              </MediaCard>
            </li>
          );
        })}
      </ul>
    </EndUserPublicLayout>
  );
};

export default BookingPage;
